import { HttpHeaders } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { catchError, from, map, Observable, of, switchMap, tap, throwError } from 'rxjs'
import { AddressSuggestions } from '../../models/AddressSuggestions'
import { DropdownOption, PreOrderDetails, StartOrderDetails } from '../../models/OrderDetails'
import { ServiceAvailability, ValidateAddressResponse } from '../../models/ServiceAvailability'
import { AppStateService } from '../../store/state/app/app.state.service'
import { OrderStateServiceWrapper } from '../../store/state/order/order.state.wrapper.service'
import { DataService } from '../data.service'
import { DeviceService } from '../device.service'
import { NotificationService } from '../notification.service'
import { Device } from '../../models/Device'
import { LoginFlowStateService } from '../../store/state/login-flow/login-flow.state.service'
import { EnvironmentService } from '../environment.service'
import { ExternalDataService } from '../external-data.service'
import { CustomerResult } from '../../models/Customer'
import { UserStateService } from '../../store/state/user/user.state.service'
import { SigfanOfferBenefitResponseHeaderModel, VIResponseModel, SigfanOriganizationSearchResponseHeaderModel, SigfanUpdateRequestModel, SigfanUpdateResponseModel, VIAccountResponse, VIAuthCheckFanDetails, VIAuthCheckFanResponse, VIAuthSendPinDetails, VIAuthSendPinResponse } from '../../models/sigfan.model'
import { WirelessOrderService } from './wireless-order.service'
import { WirelessCustomerServiceSummaryRequest } from '../../models/wireless.model'
import { CSSDisclosure } from '../../models/customer-service-summary.model'
import * as Sentry from "@sentry/angular";
import { OrderPayloadHelperService } from './order-payload-helper.service'
import { OrderStateServiceV2 } from '../../store/state/order/orderv2/order-state.service'
import { OrderFlowType } from '../../models/order/order.model'
import { OrderPaymentResponse, PaymentDetail } from './payment.service'
import { WirelessFlowStateService } from '../../store/state/order/orderv2/workflows/wireless/wireless-flow-state.service'
import { PaymentCardTypeResponse } from '../../models/order/api-models/order-payment-response.model'
import { ServiceAvailabilityResponse } from '../../models/order/api-models/order-sq-response.model'
import { OrderOQResponse } from '../../models/order/api-models/order-oq-response.model'
import { SubmitCartData } from '../../models/order/workflow/submit-cart.model'
import { LicOrderRequest, SamlResponse } from '../../models/order/saml/saml.model'
import { AgentMfaRequest, AgentMfaResponse, AgentMfaType } from '../../models/order/api-models/order-agent-mfa.model'
import { MasterBillingAccountResponse, MasterBillingAccountSearchRequest } from '../../models/order/order-service-info-collection.model'
import { CreditCheckResponse } from '../../models/order/api-models/order-cc-response.model'
import { Header as OrderResponseHeaderInfo, ResponseHeader as OrderProductResponseHeader } from '../../models/order/api-models/order-pd-response.model'
import { CustomerInformationCreditCheck } from '../../models/order/workflow/credit-check.model'
import { FeatureFlagService } from '../feature-flag.service'
import { WirelessShippingOptionATTResponse } from '../../models/order/api-models/wireless-shipping-option-att-response.model'

const url = 'https://us-autocomplete-pro.api.smarty.com/lookup?key='
const GENERIC_SSN = '111001111'

export type OrderTypeSelection = 'consumer' | 'smb' | 'smbnew';

interface CustomerOrderPayload {
  UserId: number;
  ConnId: number;
  OrderType: OrderTypeSelection;
  OrderNumber: string;
  DealerCode: string;
  ActivateDevices?: boolean;
  WirelessConfirm?: boolean;
  CustomerCreditInfo: {
    CustomerType: string;
    FirstName: string;
    LastName: string;
    ServicePhone: string;
    MobilePhone: string;
    Email: string;
    SsnNumber: string;
    Dob: string;
    PassCode: string;
    IdType: number;
    IdNumber: string;
    IdName?: string;
    IssuingAuthority: string;
    ExpirationDate: string;
    BillingPref?: string;
    BusinessName?: string;
  };
  Address: {
    Address1: string;
    Address2: string;
    City: string;
    State: string;
    Zip: string;
    CountryCode: string;
    UnitNumber?: string;
    UnitType?: string;
  };
  BillingAddress?: {
    Address1: string;
    Address2: string;
    City: string;
    State: string;
    Zip: string;
    CountryCode: string;
    UnitNumber?: string;
    UnitType?: string;
  };

  PreviousAddress?: {
    Address1: string;
    Address2: string;
    City: string;
    State: string;
    Zip: string;
    CountryCode: string;
    UnitNumber?: string;
    UnitType?: string;
  };

  CustomizationInfo: Array<{
    Category: string;
    Code: string;
    Customization?: any;
    Details?: any;
  }>;
  SecurityVerification?: {
    SecurityQuestion: string;
    SecurityAnswer: string;
    Pin: string;
    VerificationCode: string;
    AccountPin: string;
  };
  ShippingAddress?: {
    Address1: string;
    Address2: string;
    City: string;
    State: string;
    Zip: string;
    CountryCode: string;
    UnitNumber?: string;
    UnitType?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  #environmentService = inject(EnvironmentService)
  #dataService = inject(DataService)
  #externalDataService = inject(ExternalDataService)
  #appStateService = inject(AppStateService)
  orderStateService = inject(OrderStateServiceWrapper)
  orderStateServiceV2 = inject(OrderStateServiceV2)
  wirelessFlowStateService = inject(WirelessFlowStateService)

  #userStateService = inject(UserStateService)
  #deviceService = inject(DeviceService)
  #notificationService = inject(NotificationService)
  #loginFlowStateService = inject(LoginFlowStateService)
  #wirelessOrderService = inject(WirelessOrderService)
  #orderPayloadHelperService = inject(OrderPayloadHelperService)
  #featureFlagService = inject(FeatureFlagService)
  #preOrderUrl = this.#environmentService.getUrl('order/preorder/')
  #startOrderUrl = this.#environmentService.getUrl('order/startorder/')
  #serviceAvailabilityUrl = this.#environmentService.getUrl('order/checkservice/')
  #availableServicesUrl = this.#environmentService.getUrl('order/availableservice/')
  #customerVerificationItemsUrl = this.#environmentService.getUrl('order/verificationitems/')
  #customerSearchUrl = this.#environmentService.getUrl('ng/v1/customer/search/')
  #customerUpdateUrl = this.#environmentService.getUrl('ng/v1/customer/update/')
  #validateAddressUrl = this.#environmentService.getUrl('ng/v1/user/validateaddress/')
  #validateShipAddressUrl = this.#environmentService.getUrl('ng/v1/vi/validateshipaddress/')
  #getShippingOptionsUrl = this.#environmentService.getUrl('ng/v1/vi/getshippingoptions/')
  #getShippingOptionsATTUrl = this.#environmentService.getUrl('ng/v1/vi/getshippingoptionsatt/')
  #getReferralQRUrl = this.#environmentService.getUrl('ng/v1/vi/getreferralqr/')
  #validateZipUrl = this.#environmentService.getUrl('ng/v1/user/validatezip/')
  #productDetailsUrl = this.#environmentService.getUrl('ng/v1/customer/productdetails/')
  #productDetailsPlusUrl = this.#environmentService.getUrl('ng/v1/customer/productdetailsplus/')
  #productDetailsPlusAllUrl = this.#environmentService.getUrl('ng/v1/customer/productdetailsplusall/')
  #broadbandfactsemailUrl = this.#environmentService.getUrl('ng/v1/customer/broadbandfactsemail/')
  #servicesDownUrl = this.#environmentService.getUrl('order/servicedownsurvey/')
  #creditCheckUrl = this.#environmentService.getUrl('ng/v1/customer/creditcheck/')
  #creditCheckPlusUrl = this.#environmentService.getUrl('ng/v1/customer/creditcheckplus/')
  #orderQualificationUrl = this.#environmentService.getUrl('ng/v1/customer/orderqualification/')
  #createOrderUrl = this.#environmentService.getUrl('ng/v1/customer/createorder/')
  #submitOrderUrl = this.#environmentService.getUrl('ng/v1/customer/submitorder/')
  #attConfirmationUrl = this.#environmentService.getUrl('ng/v1/vi/checkattconfirmation/')
  #submitSignatureUrl = this.#environmentService.getUrl('ng/v1/customer/submitsignature/')
  #validateSerialNumberUrl = this.#environmentService.getUrl('ng/v1/customer/devicevalidate/')
  #fdeAuthenticateUrl = this.#environmentService.getUrl('ng/v1/customer/fdeauthenticate/')
  #fdeCheckStatusUrl = this.#environmentService.getUrl('ng/v1/customer/fdecheckstatus/')
  #viAuthGetDataUrl = this.#environmentService.getUrl('ng/v1/vi/authgetdata/')
  #viAuthGetAccountUrl = this.#environmentService.getUrl('ng/v1/vi/authgetaccount/')
  #viAuthCheckFanUrl = this.#environmentService.getUrl('ng/v1/vi/authcheckfan/')
  #viPaymentCardTypeUrl = this.#environmentService.getUrl('ng/v1/vi/paymentcardtype/')
  #viAuthSendPinUrl = this.#environmentService.getUrl('ng/v1/vi/authsendpin/')
  #uploadOrderSummaryUrl = this.#environmentService.getUrl('ng/v1/order/uploadordersummary/')
  #sigfanUrl = this.#environmentService.getUrl('ng/v1/vi/')
  #viPaymentUrl = this.#environmentService.getUrl('ng/v1/vi/payment/')
  #tpvStatusUrl = this.#environmentService.getUrl('ng/v1/customer/getTPVStatus/')
  #updateOfferUrl = this.#environmentService.getUrl('ng/v1/customer/updateoffer/')
  #customerSubmitCssUrl = this.#environmentService.getUrl('ng/v1/customer/submitcss/')
  #customerGetDisclosuresUrl = this.#environmentService.getUrl('ng/v1/customer/getdisclosures/')
  #processSAMLUrl = this.#environmentService.getUrl('order/processsamlorder/')
  #submitCartTPVUrl = this.#environmentService.getUrl('ng/v1/customer/submitCartForTPV/')
  #existingCustomerServices = this.#environmentService.getUrl('ng/v1/customer/existingcustomer/')
  #landlordPermissionUrl = this.#environmentService.getUrl('common/sendlandpermissionemail/')
  #agentMfaCheckUrl = this.#environmentService.getUrl('order/agentMfaCheck/')
  #masterBillingUrl = this.#environmentService.getUrl('dtvcommercial/searchaccount/')
  #oofSubmitScheduleDateUrl = this.#environmentService.getUrl('order/oofsubmitscheduledate/')
  #rc1VerifyPortNumberUrl = this.#environmentService.getUrl('ng/v1/customer/rc1VerifyPortNumber/')
  #processLicUrl = this.#environmentService.getUrl('lic/{sessionId}/launch/{launchId}');
  constructor() { }

  // Helper methods to reduce repetition
  private getSessionId(): string {
    return this.#appStateService.getSession()?.SessionID;
  }

  private getStandardHeaders(): HttpHeaders {
    return new HttpHeaders().set('Content-Type', 'application/json');
  }

  private getOrderBasicInfo() {
    return this.#orderPayloadHelperService.getOrderBasicInfo();
  }

  private getAvailableServicesSQPayloadInfo() {
    return this.#orderPayloadHelperService.getAvailableServicesSQPayloadInfo();
  }

  private getCustomer() {
    return this.#orderPayloadHelperService.getCustomer();
  }

  private processIdentificationInfo(formValue: any): {
    dob: string,
    idType: number,
    idNumber: string,
    idName: string,
    issuingAuthority: string,
    expirationDate: string,
    ssn: string
  } {
    const dob = formValue?.dobMonth ?
      `${formValue.dobMonth}/${formValue.dobDay}/${formValue.dobYear}` : undefined;

    let expirationDate = "";
    let idType = 0; // default
    let issuingAuthority = "";
    let idNumber = "";
    let idName = "";

    // if (formValue.ssnConsent) {
    idNumber = formValue?.idNumber;
    idName = formValue?.idName;
    issuingAuthority = formValue?.issuingAuthority;
    if (formValue?.expirationMonth && formValue?.expirationDay && formValue?.expirationYear) {
      expirationDate = `${formValue.expirationMonth}/${formValue.expirationDay}/${formValue.expirationYear}`;
    }

    const idTypeMap = {
      'none': 0,
      'drivers_license': 1,
      'state_issued_id': 2,
      'us_military_id': 3,
      'us_passport': 4,
      'foreign_government_id': 5
    };

    const labelIdTypeMap = {
      'Driver’s License': 1,
      'State Issued ID': 2,
      'U.S. Military ID': 3,
      'U.S. Passport': 4,
      'Foreign Government ID': 5
    };

    idType = idTypeMap[formValue?.idType] || labelIdTypeMap[formValue?.idType] || 0;
    // }

    return {
      dob,
      idType,
      idNumber,
      idName,
      issuingAuthority,
      expirationDate,
      ssn: (formValue?.noSsnCheckbox || formValue?.ssnConsent)
        ? GENERIC_SSN
        : formValue?.rawSsn === '' ? formValue?.ssn : formValue?.rawSsn
    };
  }

  wirelessSetupCustomerOrderPayload() {
    return this.buildCustomerOrderPayload(null, null, true) as any;

  }

  private buildCustomerOrderPayload(
    customerAccountForm: any = null,
    alternateShippingAddressForm: any = null,
    ignoreIdInfo: boolean = false,
    forCreditCheck: boolean = false
  ): CustomerOrderPayload {
    const { OrderNumber, ConnId, OrderType } = this.getOrderBasicInfo();
    const selectedDealerCode = this.getOrderBasicInfo().selectedDealerCode;
    const customer = this.getCustomer();
    const user = this.#userStateService.getUser();
    const creditCheckData = this.#orderPayloadHelperService.getCreditCheckStateData();
    const emailValidationData = this.#orderPayloadHelperService.getEmailValidationStateData();
    const alternateShippingAddressState = this.#orderPayloadHelperService.getAlternateShippingAddressStateData();
    const formValue = creditCheckData?.customerInformationCreditCheck;
    const creditCheckBillingAddress = creditCheckData?.billingAddress;
    const alternateBillingAddress = alternateShippingAddressState?.alternateBillingAddress;
    const previousAddress = this.#orderPayloadHelperService.getPreviousAddressStateData()?.address ?? creditCheckData?.previousAddress;
    const commStreamBillingAddress = this.orderStateServiceV2?.order()?.serviceInfoCollection?.billingAddress;
    const wirelessCode = this.#orderPayloadHelperService.getSelectedPlanCode(OrderFlowType.Wireless);
    const serviceAddressForm = this.#orderPayloadHelperService.getServiceInformationCollectionStateData()?.serviceAvailability;
    const CustomizationInfo = this.#orderPayloadHelperService.getCustomizationInfo(undefined, forCreditCheck);
    const email = emailValidationData?.email ?? creditCheckData?.customerInformationQualityCheck?.email ?? customer?.Email;
    const serviceInfoCustomer = this.#orderPayloadHelperService.getServiceInformationCollectionStateData()?.customerInformation;

    // Only process identification info if ignoreIdInfo is false
    let dob, idType, idNumber, issuingAuthority, expirationDate, ssn;
    if (!ignoreIdInfo) {
      ({
        dob,
        idType,
        idNumber,
        issuingAuthority,
        expirationDate,
        ssn
      } = this.processIdentificationInfo(formValue));
    }

    const payload: CustomerOrderPayload = {
      UserId: user.UserId,
      ConnId,
      OrderType,
      OrderNumber,
      DealerCode: selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: serviceInfoCustomer?.mobilePhone ?? customer?.MobilePhone,
        Email: email,
        SsnNumber: ssn,
        Dob: dob,
        PassCode: formValue?.passcode ? formValue.passcode : '',
        IdType: idType,
        IdNumber: idNumber,
        IssuingAuthority: issuingAuthority,
        ExpirationDate: expirationDate,
        BillingPref: creditCheckData?.customerPreferences?.billingPreference || '',
        ...(OrderType === 'smbnew' && {
          BusinessName: creditCheckData?.customerInformationQualityCheck?.businessName?.trim()
            || this.#orderPayloadHelperService.getServiceInformationCollectionStateData()?.businessInformationSMB?.businessNameSMB?.trim()
            || ''
        }),
      },
      Address: {
        Address1: customer?.ServiceAddress?.Address1,
        Address2: customer?.ServiceAddress?.Address2,
        City: customer?.ServiceAddress?.City,
        State: customer?.ServiceAddress?.State,
        Zip: customer?.ServiceAddress?.Zip,
        CountryCode: customer?.ServiceAddress?.CountryCode,
        UnitNumber: serviceAddressForm?.unitNumber || '',
        UnitType: serviceAddressForm?.unitType || ''
      },
      CustomizationInfo
    };

    // If billing address changes on credit check, we need to pass Billing Address
    if (creditCheckBillingAddress) {
      payload.BillingAddress = {
        Address1: creditCheckBillingAddress?.serviceAddress,
        Address2: '',
        City: creditCheckBillingAddress?.city,
        State: creditCheckBillingAddress?.state,
        Zip: creditCheckBillingAddress?.zip,
        CountryCode: 'USA',
        UnitType: creditCheckBillingAddress?.unitType,
        UnitNumber: creditCheckBillingAddress?.unitNumber
      }
    }

    // if alternate billing address is provided in alternate shipping address page, we need to pass this Address instead of credit check billing address
    if (alternateBillingAddress) {
      payload.BillingAddress = {
        Address1: alternateBillingAddress?.serviceAddress,
        Address2: '',
        City: alternateBillingAddress?.city,
        State: alternateBillingAddress?.state,
        Zip: alternateBillingAddress?.zipCode,
        CountryCode: 'USA',
        UnitType: alternateBillingAddress?.unitType,
        UnitNumber: alternateBillingAddress?.unitNumber
      };
    }

    // For charter, if previous address is provided during credit check, we need to pass Previous Address
    if (previousAddress) {
      payload.PreviousAddress = {
        Address1: previousAddress?.serviceAddress,
        Address2: '',
        City: previousAddress?.city,
        State: previousAddress?.state,
        Zip: previousAddress?.zip ?? previousAddress?.zipCode,
        CountryCode: 'USA',
        UnitType: previousAddress?.unitType,
        UnitNumber: previousAddress?.unitNumber
      };
    }

    // Add optional properties if data is provided
    if (customerAccountForm && Object.keys(customerAccountForm).length > 0) {
      payload.SecurityVerification = {
        SecurityQuestion: customerAccountForm.securityQuestion,
        SecurityAnswer: customerAccountForm.securityAnswer,
        Pin: customerAccountForm.pass,
        VerificationCode: "",
        AccountPin: ""
      };
    }

    if (alternateShippingAddressForm && Object.keys(alternateShippingAddressForm).length > 0) {
      payload.ShippingAddress = {
        Address1: alternateShippingAddressForm.serviceAddress,
        Address2: "",
        City: alternateShippingAddressForm.city,
        State: alternateShippingAddressForm.state,
        Zip: alternateShippingAddressForm.zipCode,
        UnitNumber: alternateShippingAddressForm.unitNumber,
        UnitType: alternateShippingAddressForm.unitType,
        CountryCode: "USA"
      };
    }

    if (wirelessCode) {
      const activateDevices: boolean = this.orderStateServiceV2.order().orderSummary?.ActivateDevices ?? false;
      const wirelessConfirm: boolean = this.orderStateServiceV2.order().orderSummary?.WirelessConfirm ?? false;
      payload.ActivateDevices = activateDevices;
      payload.WirelessConfirm = wirelessConfirm;
    }

    // For commercial stream, we need to pass Billing Address
    if (commStreamBillingAddress) {
      payload.BillingAddress = {
        Address1: commStreamBillingAddress?.serviceAddress,
        Address2: '',
        City: commStreamBillingAddress?.city,
        State: commStreamBillingAddress?.state,
        Zip: commStreamBillingAddress?.zipCode,
        CountryCode: 'USA',
        UnitType: commStreamBillingAddress?.unitType,
        UnitNumber: commStreamBillingAddress?.unitNumber
      }
    }

    return payload;
  }

  getPreOrder(): Observable<PreOrderDetails> {
    const sessionId = this.getSessionId();
    const endpoint = this.#preOrderUrl + sessionId;
    return this.#dataService.get<PreOrderDetails>(endpoint, this.getStandardHeaders())
      .pipe(tap((resp) => {
        this.orderStateService.setPreOrder(resp)
        Sentry.setTag('Order', resp.OrderNumber);
      }));
  }

  getStartOrder(orderTypeOverride?: OrderTypeSelection): Observable<StartOrderDetails> {

    if (orderTypeOverride === undefined && this.orderStateService.getStartOrder()) {
      return of(this.orderStateService.getStartOrder())
    }

    return from(this.#resolveOrderLocation()).pipe(
      switchMap((device) => {
        const ffLocationEnabled = this.#featureFlagService.isEnabled('enableNewOrderRefreshLocation');
        if (!device && ffLocationEnabled) {
          this.#notificationService.showError('Failed to retrieve location. Please try again.')
          return throwError(() => new Error('Failed to retrieve location'))
        }

        const sessionId = this.getSessionId();
        const preOrder = this.orderStateService.getPreOrder()
        const orderId = preOrder?.OrderId;

        const orderType: OrderTypeSelection = orderTypeOverride !== undefined
          ? orderTypeOverride
          : (preOrder?.OrderType as OrderTypeSelection) || 'consumer';

        const endpoint = this.#startOrderUrl + sessionId;

        const payload = {
          OrderId: orderId,
          Latitude: device.latitude,
          Longitude: device.longitude,
          GeoAccuracy: device.geoAccuracy,
          OrderType: orderType,
        };

        return this.#dataService.post<unknown, StartOrderDetails>(endpoint, payload, this.getStandardHeaders())
          .pipe(tap((resp: StartOrderDetails) => {
            this.orderStateService.setStartOrder(resp)
          }));
      })
    );
  }

  async #resolveOrderLocation(): Promise<Device | null> {
    await this.#deviceService.initDevice()

    if (!this.#deviceService.locationDisabled()) {
      const device = this.#appStateService.getDevice()
      if (this.#hasRealLocation(device)) {
        return device
      }
    }

    //if location services are optional or feature flag is disabled, we can proceed without location
    const locationOptional = (this.#loginFlowStateService.getLoginFlow()?.LocationServicesOptional
      || !this.#featureFlagService.isEnabled('enableNewOrderRefreshLocation'))
      ?? false
    if (locationOptional) {
      await this.#deviceService.initDevice(true)
      return this.#appStateService.getDevice()
    }

    return null
  }

  #isDefaultLocation(device: Device | null): boolean {
    return device?.latitude === '0' && device?.longitude === '0'
  }

  #hasRealLocation(device: Device | null): boolean {
    return !!device?.latitude && !!device?.longitude && !this.#isDefaultLocation(device)
  }

  getSuggestionAddressesFromSmarty(value: string): Observable<AddressSuggestions> {
    const key = Number(this.#appStateService.getSession()?.SmartyKey);
    const urlEndpoint = `${url}${key}&search=${value}`;
    return this.#externalDataService.get(urlEndpoint);
  }

  getCustomerVerificationItems() {
    const sessionId = this.getSessionId();
    const orderNumber = this.orderStateService.getPreOrder()?.OrderNumber;
    const endpoint = this.#customerVerificationItemsUrl + sessionId + '/' + orderNumber;
    return this.#dataService.get(endpoint, this.getStandardHeaders());
  }

  checkServiceAvailability(value: any, isMultipleAddresses: boolean) {
    const sessionId = this.getSessionId();
    const { OrderNumber, ConnId, OrderType } = this.getOrderBasicInfo();
    const selectedDealerCode = this.#orderPayloadHelperService.getOrderBasicInfo()?.selectedDealerCode;
    const { salesInformationForm, storeId, directvId } = this.getAvailableServicesSQPayloadInfo();

    const endpoint = this.#serviceAvailabilityUrl + sessionId;

    const payload = {
      OrderNumber,
      ConnId,
      OrderType,
      DealerCode: selectedDealerCode,
      CampaignID: salesInformationForm.campaign,
      DirectvID: directvId,
      StoreId: storeId,
      AppVersion: this.#environmentService.env.appVersion,
      Address: {
        MultipleAddressSelected: isMultipleAddresses,
        Address1: value.serviceAddress.street_line ? value.serviceAddress.street_line?.trim() : value.serviceAddress,
        Address2: '',
        City: value.city,
        State: value.state,
        Zip: value.zipCode,
        CountryCode: 'USA',
        unitType: value.unitType,
        unitNumber: value.unitNumber,
        structureType: value.structureType,
        structureNumber: value.structureNumber,
        floor: value.floor
      }
    };

    return this.#dataService.post<unknown, ServiceAvailability[]>(endpoint, payload, this.getStandardHeaders());
  }

  searchCustomer(customerFormValue: any, addressFormValue: any) {
    const sessionId = this.getSessionId();
    const { OrderNumber, OrderType } = this.getOrderBasicInfo();

    const endpoint = this.#customerSearchUrl + sessionId;

    const payload = {
      OrderNumber,
      OrderType,
      FirstName: customerFormValue.firstName?.trim(),
      LastName: customerFormValue.lastName?.trim(),
      PhoneNumber: customerFormValue.phone?.replaceAll('-', '').replaceAll('(', '').replaceAll(')', ''),
      Email: customerFormValue.email,
      CustomerAddress: addressFormValue.serviceAddress?.street_line ? addressFormValue.serviceAddress?.street_line : addressFormValue.serviceAddress,
      CustomerZip: addressFormValue.zipCode
    };

    return this.#dataService.post<unknown, CustomerResult[]>(endpoint, payload, this.getStandardHeaders(), false);
  }

  updateCustomer(customerFormValue: any, addressFormValue: any, isMultipleAddresses: boolean) {
    const sessionId = this.getSessionId();
    const { CustomerId, OrderNumber } = this.orderStateService.getPreOrder() || {};
    const endpoint = this.#customerUpdateUrl + sessionId;

    const payload = {
      CustomerId,
      OrderNumber,
      FirstName: customerFormValue.firstName?.trim(),
      LastName: customerFormValue.lastName?.trim(),
      PhoneNumber: customerFormValue.phone?.replaceAll('-', '').replaceAll('(', '').replaceAll(')', ''),
      Email: customerFormValue.email,
      MultipleAddressSelected: isMultipleAddresses,
      CustomerAddress: addressFormValue.serviceAddress?.street_line ? addressFormValue.serviceAddress?.street_line : addressFormValue.serviceAddress,
      CustomerCity: addressFormValue.city,
      CustomerState: addressFormValue.state,
      CustomerZip: addressFormValue.zipCode,
      UnitType: addressFormValue.unitType,
      UnitNumber: addressFormValue.unitNumber,
      StructureType: addressFormValue.structureType,
      StructureNumber: addressFormValue.structureNumber,
      Floor: addressFormValue.floor
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  getAvailableServices_SQ(customerFormValue: any, addressFormValue: any, billingAddressForm: any, commercialAccountForm: any, businessInformationFormSMB: any, masterBillingForm: any, isMultipleAddresses: boolean, isSMB: boolean) {
    const sessionId = this.getSessionId();
    const { OrderNumber, ConnId, CustomerId, selectedDealerCode, OrderType } = this.getOrderBasicInfo();
    const { referenceNumberForm, orderOptionsForm, salesInformationForm, storeId, directvId } = this.getAvailableServicesSQPayloadInfo();

    const payload = {
      OrderNumber,
      ConnId,
      OrderType,
      DealerCode: selectedDealerCode,
      ReferenceNumbers: referenceNumberForm?.map(ref => ref.reference),
      HasVerifiedId: customerFormValue?.govtApproved ?? false,
      CanServiceProviderContact: customerFormValue.canContact ?? false,
      ExistingAttCustomer: customerFormValue.isExistingCustomer === 'Yes',
      InteractionType: orderOptionsForm.interactionType,
      Location: orderOptionsForm.location,
      SalesPersonId: salesInformationForm.salesPerson,
      CampaignID: salesInformationForm.campaign,
      DirectvID: directvId,
      StoreId: storeId,
      AppVersion: this.#environmentService.env.appVersion,
      Address: {
        MultipleAddressSelected: isMultipleAddresses,
        Address1: addressFormValue.serviceAddress.street_line ? addressFormValue.serviceAddress.street_line?.trim() : addressFormValue.serviceAddress?.split('|')[0].trim(),
        Address2: '',
        City: addressFormValue.city,
        State: addressFormValue.state,
        Zip: addressFormValue.zipCode,
        CountryCode: 'USA',
        UnitType: addressFormValue.unitType,
        UnitNumber: addressFormValue.unitNumber ? addressFormValue.unitNumber : addressFormValue?.serviceAddress?.split('|')[1] ? addressFormValue.serviceAddress?.split('|')[1]?.trim() : '',
        StructureType: addressFormValue.structureType,
        StructureNumber: addressFormValue.structureNumber,
        Floor: addressFormValue.floor
      },
      BillingAddress: {
        SameAsServiceAddress: billingAddressForm?.sameAsServiceAddress ?? false,
        Address1: billingAddressForm ? billingAddressForm.serviceAddress : '',
        Address2: '',
        City: billingAddressForm ? billingAddressForm.city : '',
        State: billingAddressForm ? billingAddressForm.state : '',
        Zip: billingAddressForm ? billingAddressForm.zipCode : '',
        CountryCode: 'USA',
        UnitType: billingAddressForm?.unitType || '',
        UnitNumber: billingAddressForm?.unitNumber ? billingAddressForm.unitNumber : billingAddressForm?.serviceAddress?.split('|')[1] ? billingAddressForm.serviceAddress?.split('|')[1]?.trim() : '',
        StructureType: billingAddressForm?.structureType || '',
        StructureNumber: billingAddressForm?.structureNumber || '',
        Floor: billingAddressForm?.floor || ''
      },
      CustomerInfo: {
        CustomerId,
        CustomerType: "RESIDENTIAL",
        FirstName: customerFormValue.firstName?.trim(),
        LastName: customerFormValue.lastName?.trim(),
        Email: customerFormValue.email,
        ServicePhone: customerFormValue.phone?.replaceAll('-', '').replaceAll('(', '').replaceAll(')', ''),
        MobilePhone: (customerFormValue.alternatePhone ?? customerFormValue.contactPhone)?.replaceAll('-', '').replaceAll('(', '').replaceAll(')', ''),
        BusinessSegment: commercialAccountForm?.businessSegment || '',
        BusinessType: isSMB ? businessInformationFormSMB?.businessTypeSMB?.trim() : commercialAccountForm?.businessType || '',
        AccountType: commercialAccountForm?.accountType || '',
        BusinessName: isSMB ? businessInformationFormSMB?.businessNameSMB?.trim() : customerFormValue?.businessName?.trim() || '',
        BusinessTaxId: isSMB ? businessInformationFormSMB?.businessTaxIdSMBRaw?.trim() : '',
        BusinessEvo: commercialAccountForm?.evo || '',
        BusinessFco: commercialAccountForm?.fco || '',
        BusinessSegmentLabel: commercialAccountForm?.businessSegmentLabel || '',
        BusinessTypeLabel: commercialAccountForm?.businessTypeLabel || '',
        AccountTypeLabel: commercialAccountForm?.accountTypeLabel || '',
        ParentDealerId: masterBillingForm?.ParentDealerId || '',
        ParentCorpId: masterBillingForm?.ParentCorpId || '',
        ParentCustomerId: masterBillingForm?.ParentCustomerId || '',
        ReferalDealerId: masterBillingForm?.referringDealerId || '',
        ReferalCorpId: masterBillingForm?.referringCorpId || ''
        //#3411 as per this servicephone should be mobile phone
        //mobilePhone should be alternate phone
        //AlternatePhone: customerFormValue.alternatePhone?.replaceAll('-', '').replaceAll('(', '').replaceAll(')', '')
      }
    };

    return this.#dataService.post<unknown, ServiceAvailabilityResponse>(
      this.#availableServicesUrl + sessionId,
      payload,
      this.getStandardHeaders()
    );
  }

  validateAddress(addressFormValue: any) {
    const sessionId = this.getSessionId();
    const endpoint = this.#validateAddressUrl + sessionId;
    const unitNumber = addressFormValue.unitNumber ?? '';
    const unitType = addressFormValue.unitType ?? '';
    let secondary = unitType && unitNumber ? `${unitType} ${unitNumber}` : undefined;
    if (!secondary && unitNumber) {
      secondary = unitNumber;
    }
    const payload = {
      Street: addressFormValue.serviceAddress?.street_line ? addressFormValue.serviceAddress?.street_line : addressFormValue.serviceAddress,
      Secondary: secondary,
      City: addressFormValue.city ? addressFormValue.city : '',
      State: addressFormValue.state ? addressFormValue.state : '',
      ZipCode: addressFormValue.zipCode
    };

    return this.#dataService.post<unknown, ValidateAddressResponse>(endpoint, payload, this.getStandardHeaders());
  }

  validateShipAddress(addressFormValue: any) {
    const sessionId = this.getSessionId();
    const customer = this.getCustomer();
    const endpoint = this.#validateShipAddressUrl + sessionId;
    const creditCheckData = this.#orderPayloadHelperService.getCreditCheckStateData();
    const emailValidationData = this.#orderPayloadHelperService.getEmailValidationStateData();
    const email = emailValidationData?.email ?? creditCheckData?.customerInformationQualityCheck?.email ?? customer?.Email ?? '';
    const payload: CustomerOrderPayload = {
      UserId: this.#userStateService.getUser()?.UserId,
      ConnId: this.orderStateService.getPreOrder()?.ConnId,
      OrderNumber: this.orderStateService.getPreOrder()?.OrderNumber,
      DealerCode: this.#orderPayloadHelperService.getOrderBasicInfo()?.selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: customer?.MobilePhone,
        Email: email.trim(),
      },
      Address: {
        Address1: addressFormValue.Address1,
        Address2: '',
        City: addressFormValue.City,
        State: addressFormValue.State,
        Zip: addressFormValue.Zip,
        CountryCode: 'USA'
      },
      CustomizationInfo: {
        Category: "Wireless",
      }
    } as any

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  getShippingOptions(lineNumber) {
    const sessionId = this.getSessionId();
    const endpoint = this.#getShippingOptionsUrl + sessionId;
    const payload = this.buildCustomerOrderPayload(null, null, true);

    const allLines = this.#wirelessOrderService.getAllLinesRequestPayload();
    const currentLine = allLines.find(line => line.Number === lineNumber);

    const details = [{
      Number: currentLine.Number.toString(),
      TransactionNumber: currentLine.TransactionNumber || '',
      DeviceSku: currentLine.DeviceDetails?.DeviceSku || '',
      DeviceUpc: currentLine.DeviceDetails?.Upc || '',
      ShipType: currentLine.DeviceDetails?.ShipType || ''
    }];

    payload.CustomizationInfo = payload.CustomizationInfo.filter((info) => info.Category?.toLocaleLowerCase() === 'wireless');
    payload.CustomizationInfo[0].Details = details;

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  public getViShippingOptionsATT() {
    const sessionId = this.getSessionId();
    const endpoint = this.#getShippingOptionsATTUrl + sessionId;
    const payload = this.buildCustomerOrderPayload(null, null, true) as any

    return this.#dataService.post<unknown, WirelessShippingOptionATTResponse>(endpoint, payload, this.getStandardHeaders());
  }

  validateZip(zipCode: string) {
    const sessionId = this.getSessionId();
    const endpoint = this.#validateZipUrl + sessionId;

    const payload = {
      ZipCode: zipCode
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  getInternetProductDetails() {
    return this.getProductDetails_PD(undefined, undefined, undefined, OrderFlowType.Internet)
  }

  getVideoProductDetails() {
    return this.getProductDetails_PD(undefined, undefined, undefined, OrderFlowType.Video)
  }

  getVoiceProductDetails() {
    return this.getProductDetails_PD(undefined, undefined, undefined, OrderFlowType.Voice)
  }

  getWirelessProductDetails() {
    return this.getProductDetails_PD(undefined, undefined, undefined, OrderFlowType.Wireless)
  }

  getWirelessProductDetailsPlus(line, lineNumber, active = true) {
    this.wirelessFlowStateService.updateData({
      latestPdPlusCallHappenedOnLine: lineNumber,
      latestPdplusCallActiveOnLine: active ? lineNumber : null
    })
    return this.getProductDetails_PD(this.#productDetailsPlusUrl, lineNumber, line)
  }

  getWirelessProductDetailsPlusAll(lines: any[]) {
    const sessionId = this.getSessionId();
    const endpoint = this.#productDetailsPlusAllUrl + sessionId;
    const customer = this.getCustomer();
    const emailValidationData = this.#orderPayloadHelperService.getEmailValidationStateData();
    const creditCheckData = this.#orderPayloadHelperService.getCreditCheckStateData();
    const email = emailValidationData?.email ?? creditCheckData?.customerInformationQualityCheck?.email ?? customer?.Email ?? '';
    const { OrderNumber, ConnId, selectedDealerCode, OrderType } = this.getOrderBasicInfo();
    const code = this.orderStateServiceV2.order()?.wirelessFlow?.wirelessSetup?.selectedCustomerCode || '';

    const payload = {
      OrderNumber,
      ConnId,
      OrderType,
      DealerCode: selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: customer?.MobilePhone,
        Email: email.trim(),
        SsnNumber: "",
        Dob: ""
      },
      Address: {
        Address1: customer?.ServiceAddress.Address1,
        Address2: customer?.ServiceAddress.Address2,
        City: customer?.ServiceAddress.City,
        State: customer?.ServiceAddress.State,
        Zip: customer?.ServiceAddress.Zip,
        CountryCode: customer?.ServiceAddress.CountryCode
      },
      CustomizationInfo: [{
        Category: "Wireless",
        Lines: lines,
        Code: code
      }]
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  removeLine(lineNumber) {
    const sessionId = this.getSessionId();
    const { OrderNumber } = this.getOrderBasicInfo();
    const endpoint = this.#productDetailsPlusUrl + sessionId + '/line/' + lineNumber;
    const payload = {
      OrderNumber
    }
    return this.#dataService.delete<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  private getProductDetails_PD(pdPlusUrl: string = undefined, lineNumber: string = undefined, line: any = undefined, productType: OrderFlowType = undefined) {
    const sessionId = this.getSessionId();
    const { OrderNumber, ConnId, selectedDealerCode, OrderType } = this.getOrderBasicInfo();
    const serviceAddressForm = this.#orderPayloadHelperService.getServiceInformationCollectionStateData()?.serviceAvailability;
    const customer = this.getCustomer();
    let CustomizationInfo = this.#orderPayloadHelperService.getCustomizationInfo();
    let endpoint = this.#productDetailsUrl + sessionId + '?productType=' + (productType?.toUpperCase() ?? '');
    if (pdPlusUrl) {
      endpoint = pdPlusUrl + sessionId + '/line/' + lineNumber;
      CustomizationInfo = this.#orderPayloadHelperService.getWirelessProductPlusDetailsCustomizationInfo(line);
    }

    const creditCheckData = this.#orderPayloadHelperService.getCreditCheckStateData();
    const email = creditCheckData?.customerInformationQualityCheck?.email ?? customer?.Email ?? '';

    const payload = {
      OrderNumber,
      ConnId,
      OrderType,
      DealerCode: selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: customer?.MobilePhone,
        Email: email.trim(),
        SsnNumber: "",
        Dob: ""
      },
      Address: {
        Address1: customer?.ServiceAddress?.Address1,
        Address2: customer?.ServiceAddress?.Address2,
        City: customer?.ServiceAddress?.City,
        State: customer?.ServiceAddress?.State,
        Zip: customer?.ServiceAddress?.Zip,
        CountryCode: customer?.ServiceAddress?.CountryCode,
        UnitNumber: serviceAddressForm?.unitNumber || '',
        UnitType: serviceAddressForm?.unitType || ''
      },
      CustomizationInfo: CustomizationInfo
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  sendBroadbandFactsEmail(broadbandFactsLink: string, Email: string) {
    const sessionId = this.getSessionId();
    const CustomerId = this.orderStateService.getPreOrder()?.CustomerId;

    const payload = {
      CustomerId,
      Email,
      Link: broadbandFactsLink
    };

    const endpoint = this.#broadbandfactsemailUrl + sessionId;
    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  submitServicesDownForm(value: any) {
    const sessionId = this.getSessionId();
    const { OrderNumber, OrderType } = this.getOrderBasicInfo();

    const payload = {
      OrderNumber,
      OrderType,
      Internet: value.internet,
      Video: value.video,
      Wireless: value.wireless,
      NumberOfLines: value.numberOfLines,
      Comments: value.comments,
      DeviceTypes: {
        SmartPhone: value.smartphone,
        TabletWearable: value.tablet,
        Hotspot: value.hotspot
      }
    };

    const endpoint = this.#servicesDownUrl + sessionId;
    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  getCreditCheck_CC(formValue: CustomerInformationCreditCheck): Observable<CreditCheckResponse> {
    const sessionId = this.getSessionId();
    const endpoint = this.#creditCheckUrl + sessionId;

    // We use a simpler payload here without customization
    const payload = this.buildCustomerOrderPayload(null, null, false, true);

    // Override with the provided form values
    const idInfo = this.processIdentificationInfo(formValue);
    payload.CustomerCreditInfo.Dob = idInfo.dob;
    payload.CustomerCreditInfo.SsnNumber = idInfo.ssn;
    payload.CustomerCreditInfo.IdType = idInfo.idType;
    payload.CustomerCreditInfo.IdNumber = idInfo.idNumber;
    if (idInfo.idName && idInfo.idName.length > 0) {
      payload.CustomerCreditInfo.IdName = idInfo.idName;
    }
    payload.CustomerCreditInfo.IssuingAuthority = idInfo.issuingAuthority;
    payload.CustomerCreditInfo.ExpirationDate = idInfo.expirationDate;

    return this.#dataService.post<unknown, CreditCheckResponse>(endpoint, payload, this.getStandardHeaders())
      .pipe(switchMap(resp => this.#uoAfterCreditCheck(resp)));
  }

  #uoAfterCreditCheck<T>(resp: T): Observable<T> {
    return (resp as any)?.OrderRequirements?.Internet?.UoApiAfterCreditCheck
      ? this.updateOffers_UO().pipe(map(() => resp))
      : of(resp);
  }

  // need this API for flows where creditCheckRequired flag is false from PD or UO order requirements.
  // We don't navigate to CC page and don't collect any info from user but still need to run CC in the background
  getCreditCheck_CC_Optional() {
    const sessionId = this.getSessionId();
    const endpoint = this.#creditCheckUrl + sessionId;
    const payload = this.buildCustomerOrderPayload(null, null, false, true);
    payload.CustomerCreditInfo.Dob = "";
    payload.CustomerCreditInfo.SsnNumber = "";
    // payload.CustomerCreditInfo.IdType = 0;
    payload.CustomerCreditInfo.IdNumber = "";
    payload.CustomerCreditInfo.IdName = "";
    payload.CustomerCreditInfo.IssuingAuthority = "";
    payload.CustomerCreditInfo.ExpirationDate = "";
    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  fdeAuthenticate(req) {
    const sessionId = this.getSessionId()
    const endpoint = this.#fdeAuthenticateUrl + sessionId
    const payload = this.buildCustomerOrderPayload() as any
    payload.AuthenticateDetails = req
    // Destructure to remove the unwanted key
    const cleanedPayload = this.omit(payload, ['CustomizationInfo'])
    return this.#dataService.post<unknown, unknown>(endpoint, cleanedPayload, this.getStandardHeaders())
  }

  fdeCheckStatus(req) {
    const sessionId = this.getSessionId()
    const endpoint = this.#fdeCheckStatusUrl + sessionId
    const payload = this.buildCustomerOrderPayload() as any
    payload.AuthenticateDetails = req
    // Destructure to remove the unwanted key
    const cleanedPayload = this.omit(payload, ['CustomizationInfo'])
    return this.#dataService.post<unknown, unknown>(endpoint, cleanedPayload, this.getStandardHeaders())
  }

  viAuthGetData(req) {
    const sessionId = this.getSessionId()
    const endpoint = this.#viAuthGetDataUrl + sessionId
    const payload = this.buildCustomerOrderPayload(null, null, true) as any
    payload.CustomizationInfo = {
      Category: "Wireless",
      Details: req
    }
    return this.#dataService.post<unknown, VIResponseModel<VIAccountResponse>>(endpoint, payload, this.getStandardHeaders())
  }

  validateCardType(cardNumber: string, flowType: OrderFlowType) {
    const sessionId = this.getSessionId()
    const endpoint = this.#viPaymentCardTypeUrl + sessionId
    const payload = this.buildCustomerOrderPayload(null, null, true) as any
    payload.CustomizationInfo = {
      Category: flowType,
      CardNumber: cardNumber
    }
    return this.#dataService.post<unknown, PaymentCardTypeResponse>(endpoint, payload, this.getStandardHeaders())
  }

  viAuthGetAccount(req) {
    const sessionId = this.getSessionId()
    const endpoint = this.#viAuthGetAccountUrl + sessionId
    const payload = this.buildCustomerOrderPayload(null, null, true) as any
    payload.CustomizationInfo = {
      Category: "Wireless",
      Details: req
    }
    return this.#dataService.post<unknown, VIResponseModel<VIAccountResponse>>(endpoint, payload, this.getStandardHeaders())
      .pipe(
        tap((resp) => {
          const wirelessResponse = resp?.ResponseHeader?.ProductResponse?.find(r => r.Type === 'WIRELESS');
          if (wirelessResponse) {
            resp.ResponseHeader.WirelessResponse = wirelessResponse;
          }
        })
      );
  }

  viAuthCheckFan(details: VIAuthCheckFanDetails): Observable<VIAuthCheckFanResponse> {
    const sessionId = this.getSessionId()
    const endpoint = this.#viAuthCheckFanUrl + sessionId
    const payload = {
      ...this.buildCustomerOrderPayload(null, null, true),
      CustomizationInfo: {
        Category: 'Wireless',
        Details: details
      }
    }

    return this.#dataService.post<typeof payload, VIAuthCheckFanResponse>(endpoint, payload, this.getStandardHeaders())
  }

  viSendPin(req: VIAuthSendPinDetails): Observable<VIAuthSendPinResponse> {
    const sessionId = this.getSessionId();
    const endpoint = this.#viAuthSendPinUrl + sessionId;
    const payload = this.buildCustomerOrderPayload(null, null, true) as any;
    payload.CustomizationInfo = {
      Category: "Wireless",
      Details: req
    }
    return this.#dataService.post<unknown, VIAuthSendPinResponse>(endpoint, payload, this.getStandardHeaders());
  }

  omit<T extends object, K extends keyof T>(obj: T, keys: K[]): Omit<T, K> {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (!keys.includes(key as K)) {
        (acc as any)[key] = value;
      }
      return acc;
    }, {} as Omit<T, K>);
  }

  getCreditCheck_CCPlus(questionAnswers: any, creditCards: any, payments: any, ccPlusAction: string, category: OrderFlowType) {
    const sessionId = this.#appStateService.getSession()?.SessionID;
    const endpoint = this.#creditCheckPlusUrl + sessionId;
    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json');

    const payload = this.buildCustomerOrderPayload(null, null, false, true);

    // Augment the matching category entry with CCPlus-specific fields, leave others untouched
    payload.CustomizationInfo = payload.CustomizationInfo?.map((entry: any) => {
      if (entry.Category !== category) return entry;
      return {
        ...entry,
        CCPlusAction: ccPlusAction,
        CreditAnswers: questionAnswers,
        Payments: payments || [],
        CreditCards: creditCards || [],
      };
    });

    return this.#dataService.post<unknown, unknown>(endpoint, payload, reqHeaders)
      .pipe(switchMap(resp => this.#uoAfterCreditCheck(resp)));
  }

  getCreditCheck_CCPlus_onlyAction(ccPlusAction: string, category: OrderFlowType) {
    const sessionId = this.#appStateService.getSession()?.SessionID;
    const endpoint = this.#creditCheckPlusUrl + sessionId;
    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json');

    const payload = this.buildCustomerOrderPayload(null, null, false, true);
    payload.CustomizationInfo = payload.CustomizationInfo?.map((entry: any) => {
      if (entry.Category !== category) return entry;
      return {
        ...entry,
        CCPlusAction: ccPlusAction
      };
    });

    return this.#dataService.post<unknown, unknown>(endpoint, payload, reqHeaders)
      .pipe(switchMap(resp => this.#uoAfterCreditCheck(resp)));
  }

  getOrderQualificationData_OQ() {
    const sessionId = this.getSessionId();
    const endpoint = this.#orderQualificationUrl + sessionId;
    const forms = this.orderStateServiceV2.order();
    // const { customerAccountForm, alternateShippingAddressForm } = forms;
    const customerAccountForm = forms?.customerAccountSetup?.customerAccountForm;
    const alternateShippingAddressForm = forms?.alternateShippingAddress?.alternateShippingAddress;

    const payload = this.buildCustomerOrderPayload(
      customerAccountForm,
      alternateShippingAddressForm
    );

    return this.#dataService.post<unknown, OrderOQResponse>(endpoint, payload, this.getStandardHeaders());
  }

  createOrder_CO() {
    const sessionId = this.getSessionId();
    const endpoint = this.#createOrderUrl + sessionId;
    const forms = this.orderStateServiceV2.order();
    // const { customerAccountForm, alternateShippingAddressForm } = forms;
    const customerAccountForm = forms?.customerAccountSetup?.customerAccountForm;
    const alternateShippingAddressForm = forms?.alternateShippingAddress?.alternateShippingAddress;

    const payload = this.buildCustomerOrderPayload(
      customerAccountForm,
      alternateShippingAddressForm
    );

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  submitOrder_SO() {
    const sessionId = this.getSessionId();
    const endpoint = this.#submitOrderUrl + sessionId;
    const forms = this.orderStateServiceV2.order();
    // const { customerAccountForm, alternateShippingAddressForm } = forms;
    const customerAccountForm = forms?.customerAccountSetup?.customerAccountForm;
    const alternateShippingAddressForm = forms?.alternateShippingAddress?.alternateShippingAddress;
    const appointments = this.buildAppointments();

    const payload = {
      ...this.buildCustomerOrderPayload(
        customerAccountForm,
        alternateShippingAddressForm
      ),
      ...appointments
    };

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  oofSubmitScheduleDate(appointment: any, comments?: string): Observable<{ Header?: OrderResponseHeaderInfo; ResponseHeader?: OrderProductResponseHeader }> {
    const sessionId = this.getSessionId();
    const endpoint = this.#oofSubmitScheduleDateUrl + sessionId;

    const payload = {
      ...this.buildCustomerOrderPayload(),
      ApptType: appointment?.InstallType,
      ApptId: appointment?.id?.toString(),
      ApptStartDateTime: appointment?.StartDate,
      ApptEndDateTime: appointment?.EndDate,
      InstallerComment: comments ?? ''
    };

    return this.#dataService.post<unknown, { Header?: OrderResponseHeaderInfo; ResponseHeader?: OrderProductResponseHeader }>(endpoint, payload, this.getStandardHeaders());
  }

  checkAttconfirmation() {
    const sessionId = this.getSessionId();
    const endpoint = this.#attConfirmationUrl + sessionId;
    const payload = this.buildCustomerOrderPayload() as any;

    // API expects only Category property, not the full array
    if (payload.CustomizationInfo && Array.isArray(payload.CustomizationInfo) && payload.CustomizationInfo.length > 0) {
      payload.CustomizationInfo = {
        Category: 'Wireless'
      };
    }

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  submitSignature(signatureData?: any) {
    const sessionId = this.getSessionId();
    const endpoint = this.#submitSignatureUrl + sessionId;
    const { OrderNumber } = this.getOrderBasicInfo();
    const payload = { OrderNumber, ...signatureData }

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  validateSerialNumber(serailNumbers) {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const { OrderNumber, ConnId } = this.orderStateService.getPreOrder()
    const selectedDealerCode = this.#orderPayloadHelperService.getOrderBasicInfo()?.selectedDealerCode
    const customer = this.getCustomer();
    const user = this.#userStateService.getUser()
    const endpoint = this.#validateSerialNumberUrl + sessionId
    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json');
    const serviceAddressForm = this.#orderPayloadHelperService.getServiceInformationCollectionStateData()?.serviceAvailability;

    const payload = {
      UserId: user.UserId,
      ConnId,
      OrderNumber: OrderNumber,
      DealerCode: selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: customer?.FirstName?.trim(),
        LastName: customer?.LastName?.trim(),
        ServicePhone: customer?.ServicePhone,
        MobilePhone: customer?.MobilePhone,
        Email: customer?.Email,
        SsnNumber: '',
        Dob: customer?.Dob,
      },
      Address: {
        Address1: customer?.ServiceAddress?.Address1,
        Address2: customer?.ServiceAddress?.Address2,
        City: customer?.ServiceAddress?.City,
        State: customer?.ServiceAddress?.State,
        Zip: customer?.ServiceAddress?.Zip,
        CountryCode: customer?.ServiceAddress?.CountryCode,
        UnitNumber: serviceAddressForm?.unitNumber || '',
        UnitType: serviceAddressForm?.unitType || ''
      },
      ValidateDevices: serailNumbers
    }

    return this.#dataService.post<unknown, unknown>(endpoint, payload, reqHeaders)
  }

  buildAppointments() {
    const appointments = [];
    const schedulingState = this.orderStateServiceV2.order()?.installationScheduling;
    const internetAppointment = schedulingState?.selectedInternetAppointment;
    const internetAppointmentSecondPreference = schedulingState?.selectedInternetAppointment2ndPreference;
    const dtvAppointment = schedulingState?.selectedDirectTvAppointment;
    const comments = schedulingState?.comments;

    if (internetAppointment) {
      appointments.push({
        ApptId: internetAppointment.id?.toString(),
        ApptType: internetAppointment.InstallType,
        AppointmentStart: internetAppointment.StartDate,
        AppointmentEnd: internetAppointment.EndDate,
        InstallerComment: comments
      });
    }

    if (internetAppointmentSecondPreference) {
      appointments.push({
        ApptId: internetAppointmentSecondPreference.id?.toString(),
        ApptType: internetAppointmentSecondPreference.InstallType,
        AppointmentStart: internetAppointmentSecondPreference.StartDate,
        AppointmentEnd: internetAppointmentSecondPreference.EndDate,
        InstallerComment: comments
      });
    }

    if (dtvAppointment) {
      appointments.push({
        ApptId: dtvAppointment.id?.toString(),
        ApptType: dtvAppointment.InstallType,
        AppointmentStart: dtvAppointment.StartDate,
        AppointmentEnd: dtvAppointment.EndDate,
        InstallerComment: comments
      })
    }

    return {
      Appointments: appointments
    }
  }

  getSigfanNumber(): Observable<string> {
    return of('123456789');
  }

  getOrganizations(searchKeyword: string, type: string): Observable<DropdownOption[]> {

    if (type === 'nurse_physician_appreciation') {
      return of([
        { "name": "Mercy", "value": "mercy" },
        { "name": "Mercy General Hospital", "value": "mercy_general_hospital" },
        { "name": "Mercy Gilbert Medical Center (Gilbert, AZ)", "value": "mercy_gilbert_medical_center" },
        { "name": "Mercy General Hospital (Sacramento, CA)", "value": "mercy_general_hospital_sacramento" },
        { "name": "Mercy Hospital (Coon Rapids, MN)", "value": "mercy_hospital_coon_rapids" },
        { "name": "Mercy Hospital (Buffalo, NY)", "value": "mercy_hospital_buffalo" }
      ]);
    }
    else if (type === 'student') {
      return of([
        { "name": "University of Iowa", "value": "university_of_iowa" },
        { "name": "Iowa State University", "value": "iowa_state_university" },
        { "name": "Drake University", "value": "drake_university" },
        { "name": "University of Northern Iowa", "value": "university_of_northern_iowa" }
      ]);
    }
    else if (type === 'employee') {
      return of([
        { "name": "Employee 1", "value": "employee_1" },
        { "name": "Employee 2", "value": "employee_2" },
        { "name": "Employee 3", "value": "employee_3" }
      ]);
    }
    else if (type === 'first_responder_appreciation') {
      return of([
        { "name": "Paramedic", "value": "paramedic" },
        { "name": "EMT", "value": "emt" },
        { "name": "Firefighter", "value": "firefighter" },
        { "name": "Police Officer", "value": "police_officer" }
      ]);
    }
    else if (type === 'teacher_appreciation') {
      return of([
        { "name": "Teacher 1", "value": "teacher_1" },
        { "name": "Teacher 2", "value": "teacher_2" },
        { "name": "Teacher 3", "value": "teacher_3" }
      ]);
    }
    else {
      return of([]);
    }
  }

  uploadOrderSummary(data?: FormData): Observable<unknown> {
    const sessionId = this.getSessionId();
    const endpoint = this.#uploadOrderSummaryUrl + sessionId;
    return this.#dataService.post<unknown, unknown>(endpoint, data);
  }

  getSigfanOffer(request: { Category: string, FanNumber: string }): Observable<VIResponseModel<SigfanOfferBenefitResponseHeaderModel>> {
    const sessionId = this.getSessionId();
    const endpoint = `${this.#sigfanUrl}fanoffers/${sessionId}`;
    const payload = this.buildCustomerOrderPayload(null, null, true) as any;
    payload.CustomizationInfo = request;

    return this.#dataService.post<unknown, VIResponseModel<SigfanOfferBenefitResponseHeaderModel>>(endpoint, payload, this.getStandardHeaders()).pipe(
      map(results => {
        if (results.ResponseHeader && results.ResponseHeader.ProductResponse && results.ResponseHeader.ProductResponse.length > 0) {
          results.ResponseHeader.ProductResponse.forEach(result => {
            if (result.JsonResponse && typeof result.JsonResponse === 'string') {
              result.JsonResponse = this.decodeAndParseJson(result.JsonResponse as string);
            }
          });

          const wirelessResponse = results.ResponseHeader.ProductResponse.find(r => r.Type === 'WIRELESS');
          if (wirelessResponse) {
            results.ResponseHeader.WirelessResponse = wirelessResponse;
            if (wirelessResponse.JsonResponse) {
              results.ResponseHeader.WirelessResponse.BannerHeading = wirelessResponse.JsonResponse.root.content?.banner?.heading;
              results.ResponseHeader.WirelessResponse.OfferHeadings = wirelessResponse.JsonResponse.root.content?.offers
                ?.filter(offer => offer.offerHeading)
                .map(offer => offer.offerHeading);
            }
          }
        }
        return results;
      }),
      catchError((error) => {
        console.error('Error in getSigfanOffer:', error);
        return throwError(() => error);
      })
    );
  }

  getFanSearchResults(search: { Category: string, FanGroup: string, FanOrgName: string }): Observable<VIResponseModel<SigfanOriganizationSearchResponseHeaderModel>> {
    const sessionId = this.getSessionId();
    const endpoint = `${this.#sigfanUrl}fansearch/${sessionId}`;
    const payload = this.buildCustomerOrderPayload(null, null, true) as any;
    payload.CustomizationInfo = search;
    return this.#dataService.post<unknown, VIResponseModel<SigfanOriganizationSearchResponseHeaderModel>>(endpoint, payload, this.getStandardHeaders())
      .pipe(
        tap(results => {
          if (results.ResponseHeader && results.ResponseHeader.ProductResponse && results.ResponseHeader.ProductResponse.length > 0) {
            results.ResponseHeader.ProductResponse.forEach(result => {
              if (result.JsonResponse && typeof result.JsonResponse === 'string') {
                result.JsonResponse = this.decodeAndParseJson(result.JsonResponse as string);
              }
            });

            results.ResponseHeader.ProductResponse = results.ResponseHeader.ProductResponse.filter(r => r.Type === 'WIRELESS' && r.JsonResponse);
          }
        })
      );
  }

  submitSigfanOffer(request: SigfanUpdateRequestModel): Observable<VIResponseModel<SigfanUpdateResponseModel>> {
    const sessionId = this.getSessionId();
    const endpoint = `${this.#sigfanUrl}updatefan/${sessionId}`;
    const payload = this.buildCustomerOrderPayload(null, null, true) as any;
    payload.CustomizationInfo = request;
    return this.#dataService.post<unknown, VIResponseModel<SigfanUpdateResponseModel>>(endpoint, payload, this.getStandardHeaders())
      .pipe(
        tap(response => {
          // Handle the response if needed
          const wirelessResponse = response?.ResponseHeader?.ProductResponse?.find(r => r.Type === 'WIRELESS');
          response.ResponseHeader.WirelessResponse = wirelessResponse;
        }),
      );
  }

  submitViPayment(paymentDetails: [], achPaymentDetails: PaymentDetail[]) {
    const sessionId = this.getSessionId();
    const endpoint = this.#viPaymentUrl + sessionId;
    const payload = this.buildCustomerOrderPayload(null, null, true) as any;
    const { OrderNumber, ConnId } = this.orderStateService.getPreOrder();
    payload.CustomizationInfo = {
      PaymentDetails: paymentDetails,
      ACHPaymentDetails: achPaymentDetails,
      Category: "Wireless",
      ConnId: ConnId,
      OrderNumber: OrderNumber
    }
    return this.#dataService.post<unknown, OrderPaymentResponse>(endpoint, payload, this.getStandardHeaders())
  }

  getTPVStatus(orderNumber: string): Observable<any> {
    const sessionId = this.getSessionId();
    const endpoint = this.#tpvStatusUrl + sessionId;
    const payload = {
      OrderNumber: orderNumber,
    }
    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders())
  }

  submitCustomerCssData(customerCssData: WirelessCustomerServiceSummaryRequest): Observable<any> {
    const sessionId = this.getSessionId();
    const endpoint = this.#customerSubmitCssUrl + sessionId;
    const payload = customerCssData;
    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders())
  }

  sendLandlordEmail(email: string): Observable<any> {
    const sessionId = this.getSessionId();
    const endpoint = this.#landlordPermissionUrl + sessionId;
    const payload = {
      SendTo: email
    }
    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders())
  }

  updateOffers_UO() {
    const sessionId = this.getSessionId();
    const productType = this.orderStateServiceV2.currentFlowType();
    const endpoint = this.#updateOfferUrl + sessionId + '?productType=' + (productType?.toUpperCase() ?? '');
    const forms = this.orderStateServiceV2.order();
    const customerAccountForm = forms?.customerAccountSetup?.customerAccountForm;
    const alternateShippingAddressForm = forms?.alternateShippingAddress?.alternateShippingAddress;
    const payload = this.buildCustomerOrderPayload(customerAccountForm, alternateShippingAddressForm) as any;
    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders())
      .pipe(tap(resp => this.orderStateService.setUpdateOffers(resp)))
  }

  getCustomerDisclosures(): Observable<CSSDisclosure[]> {
    const sessionId = this.getSessionId();
    const endpoint = this.#customerGetDisclosuresUrl + sessionId;
    const wirelessCustomizations = [];
    const internetCustomizations = [];
    const customizations = [];
    if (wirelessCustomizations && wirelessCustomizations.length > 0) {
      customizations.push(...wirelessCustomizations);
    }
    if (internetCustomizations && internetCustomizations.length > 0) {
      customizations.push(...internetCustomizations);
    }

    const payload = {
      OrderNumber: this.orderStateService.getPreOrder().OrderNumber,
      CustomizationInfo: customizations
    }
    return this.#dataService.post<unknown, CSSDisclosure[]>(endpoint, payload, this.getStandardHeaders());
  }

  processSAML(uniqueId: string): Observable<SamlResponse> {
    const sessionId = this.getSessionId();
    const endpoint = `${this.#processSAMLUrl}${sessionId}/${uniqueId}`;
    return this.#dataService.post<unknown, SamlResponse>(endpoint, null, this.getStandardHeaders())
  }

  processLaunchInContextId(launchId: string, request: LicOrderRequest): Observable<SamlResponse> {
    const sessionId = this.getSessionId();
    const endpoint = this.#processLicUrl.replace('{sessionId}', sessionId).replace('{launchId}', launchId);
    return this.#dataService.post<LicOrderRequest, SamlResponse>(endpoint, request, this.getStandardHeaders());
  }

  agentMfaCheck(type: AgentMfaType): Observable<AgentMfaResponse> {
    const sessionId = this.getSessionId();
    const endpoint = this.#agentMfaCheckUrl + sessionId;
    const device = this.#appStateService.getDevice();
    const user = this.#userStateService.getUser();
    const { OrderNumber, ConnId, SalesRepID } = this.getOrderBasicInfo();

    const payload: AgentMfaRequest = {
      UserId: user?.UserId,
      ShowAll: false,
      Data: {
        ConnID: ConnId,
        OrderNumber,
        SalesRepID: SalesRepID,
        Latitude: device?.latitude,
        Longitude: device?.longitude
      },
      Type: type
    };

    return this.#dataService.post<AgentMfaRequest, AgentMfaResponse>(endpoint, payload, this.getStandardHeaders());
  }


  private decodeAndParseJson(encoded: string): any {
    // Replace HTML entities with their actual characters
    const decoded = encoded
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
    // Parse JSON
    return JSON.parse(decoded);
  }

  getReferralQrCode() {
    const sessionId = this.getSessionId();
    const orderInfo = this.orderStateServiceV2.order();
    const endpoint = this.#getReferralQRUrl + sessionId;

    const payload: CustomerOrderPayload = {
      UserId: this.#userStateService.getUser()?.UserId,
      ConnId: this.orderStateService.getPreOrder()?.ConnId,
      OrderNumber: this.orderStateService.getPreOrder()?.OrderNumber,
      DealerCode: this.#orderPayloadHelperService.getOrderBasicInfo()?.selectedDealerCode,
      CustomerCreditInfo: {
        CustomerType: "RESIDENTIAL",
        FirstName: orderInfo?.serviceInfoCollection.customerInformation.firstName,
        LastName: orderInfo?.serviceInfoCollection?.customerInformation?.lastName,
        ServicePhone: orderInfo?.serviceInfoCollection?.customerInformation?.phone,
        MobilePhone: "",
        Email: orderInfo?.serviceInfoCollection?.customerInformation?.email,
      },
      Address: {
        Address1: orderInfo?.serviceInfoCollection?.serviceAvailability?.serviceAddress,
        Address2: "",
        City: orderInfo?.serviceInfoCollection?.serviceAvailability?.city,
        State: orderInfo?.serviceInfoCollection?.serviceAvailability?.state,
        Zip: orderInfo?.serviceInfoCollection?.serviceAvailability.zipCode,
        CountryCode: "USA"
      },
      CustomizationInfo: {
        Category: "WIRELESS"
      }
    } as any

    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders())
  }

  submitCartForTPV(tpvData: SubmitCartData): Observable<any> {
    const sessionId = this.getSessionId();
    const endpoint = this.#submitCartTPVUrl + sessionId;
    const payload = tpvData;
    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders())
  }

  getExistingCustomerServices(formValue: any): Observable<any> {
    const sessionId = this.getSessionId();
    const { OrderNumber, ConnId } = this.getOrderBasicInfo();
    const selectedDealerCode = this.getOrderBasicInfo().selectedDealerCode;
    const customer = this.orderStateServiceV2.order()?.serviceInfoCollection?.customerInformation;
    const serviceAddressForm = this.#orderPayloadHelperService.getServiceInformationCollectionStateData()?.serviceAvailability;
    const endpoint = this.#existingCustomerServices + sessionId;

    // Use formValue to populate CustomizationInfo
    const payload = {
      "UserId": this.#userStateService.getUser()?.UserId,
      "ConnId": ConnId,
      "DealerCode": selectedDealerCode,
      "OrderNumber": OrderNumber,
      "CustomerCreditInfo": {
        "CustomerType": "RESIDENTIAL",
        "FirstName": customer?.firstName?.trim(),
        "LastName": customer?.lastName?.trim(),
        "ServicePhone": customer?.phone?.trim(),
        "MobilePhone": "",
        "Email": customer?.email?.trim()
      },
      "Address": {
        "Address1": serviceAddressForm?.serviceAddress?.trim(),
        "Address2": "",
        "City": serviceAddressForm?.city?.trim(),
        "State": serviceAddressForm?.state?.trim(),
        "Zip": serviceAddressForm?.zipCode?.trim(),
        "CountryCode": "USA"
      },
      "CustomizationInfo": {
        "CustomerBAN": formValue?.banNumber || "",
        "CustomerAccountPin": formValue?.accountPin || "",
        "Last4SSN": formValue?.last4Ssn || ""
      }
    };
    return this.#dataService.post<unknown, unknown>(endpoint, payload, this.getStandardHeaders());
  }

  searchMasterBillingAccounts(searchRequest: MasterBillingAccountSearchRequest): Observable<MasterBillingAccountResponse> {
    const sessionId = this.getSessionId();
    const endpoint = this.#masterBillingUrl + sessionId;
    return this.#dataService.post<MasterBillingAccountSearchRequest, MasterBillingAccountResponse>(endpoint, searchRequest, this.getStandardHeaders());
  }

  rc1VerifyPortNumber(formValue: any, lineNumber: number): Observable<any> {
    const sessionId = this.getSessionId();
    const endpoint = `${this.#rc1VerifyPortNumberUrl}${sessionId}/Line/${lineNumber}`;
    const payload = this.buildCustomerOrderPayload(null, null, true) as any;

    payload.CustomizationInfo = {
      TransactionNumber: (formValue?.currentNumber ?? '').replace(/\D/g, ''),
      AdditionalContactNumber: formValue?.additionalContactNumber ?? '',
      PinOrPassword: formValue?.accountPin ?? '',
      OSPAccountNumber: formValue?.accountNumber ?? '',
      OSPName: formValue?.accountName ?? ''
    };

    return this.#dataService.post<unknown, any>(endpoint, payload, this.getStandardHeaders());
  }
}
